import * as msal from '@azure/msal-browser';
import { MsalAccountIdentityHints } from './officeIdentityContextResolver';
import { localSettingsService } from '../../services/localSettingsService';
import ServiceConstantsProvider from '../serviceConstantsProvider';
import { AuthenticationResult, AuthError, ServerError } from '@azure/msal-browser';

type BaseAuthRequest = Omit<msal.PopupRequest, 'prompt'> & Partial<msal.SsoSilentRequest>;

export class MsalAuthService {
    private msalInstance!: msal.IPublicClientApplication;
    private identityHints?: MsalAccountIdentityHints;
    readonly entraIDRequestScopes = ['openid', 'profile'];
    readonly entraAccessRequestScopes = ['Mail.Read'];
    readonly allEntraScopes = [...this.entraIDRequestScopes, ...this.entraAccessRequestScopes];

    /**
     * Creates a new instance of the MSAL auth service.
     * @param identityHints Identity hints used for account resolution.
     * @param msalInstance Optional injected MSAL instance (for testing/mocking).
     */
    constructor(identityHints: MsalAccountIdentityHints, msalInstance?: msal.IPublicClientApplication) {
        // Dependancy injection for testing
        if (msalInstance) this.msalInstance = msalInstance;

        this.identityHints = identityHints;
    }

    /**
     * Initialises the MSAL client, preferring Nested App Auth (NAA) if available.
     *
     * @throws If a msal instance can't be created.
     */
    public async init(useNaa = true): Promise<void> {
        const { clientId } = localSettingsService.getLocalSettings();

        const config: msal.Configuration = {
            auth: {
                clientId: clientId,
                authority: ServiceConstantsProvider.getLoginAuthority(),
                redirectUri: ServiceConstantsProvider.getAuthUrl(),
            },
            cache: {
                cacheLocation: 'sessionStorage',
            },
        };

        if (useNaa && Office.context.requirements.isSetSupported('NestedAppAuth', '1.1')) {
            console.debug('NAA is supported, Initialising MSAL with NAA.');

            try {
                this.msalInstance = await msal.createNestablePublicClientApplication(config);
                console.info('Initialised MSAL with Nested App Auth.');
            } catch (error) {
                console.debug('MSAL initialisation with NAA failed.', error);
            }
        }

        if (!this.msalInstance) {
            console.debug('Using standard MSAL (NAA unavailable or failed).');

            try {
                this.msalInstance = new msal.PublicClientApplication(config);
                await this.msalInstance.initialize();

                console.info('Initialised standard MSAL.');
            } catch (error) {
                console.debug('Standard MSAL initialisation failed.', error);
            }
        }

        if (!this.msalInstance) {
            throw new MsalAuthError(MsalAuthErrorCode.InitFailed);
        }
        console.info('MSAL initialised');
    }

    /** Returns a stable user identifier for use in caching and token scoping. */
    public getUserId(): string {
        const userId =
            this.identityHints?.homeAccountId?.toLowerCase() ||
            this.identityHints?.loginHint?.toLowerCase() ||
            'anonymous';

        if (userId === 'anonymous') {
            console.warn('getUserId() fell back to "anonymous" — no identity hints present.');
        } else {
            console.debug(`getUserId() using identity hint ${userId}`);
        }
        return userId;
    }

    /**
     * Authenticates the user using MSAL by first attempting silent SSO,
     * then falling back to an interactive login if needed.
     *
     * @returns A promise that resolves with the authenticated MSAL account info.
     * @throws If authentication fails via both silent and interactive methods.
     */
    public async authenticateUser(): Promise<msal.AccountInfo> {
        console.info('Authenticating the user.');
        let loggedInAccount: msal.AccountInfo | undefined;

        // Check for a cached account first
        const cachedAccount = this.getCachedAccount() || undefined;

        // Build the msal request and parameters
        const baseRequest: BaseAuthRequest = {
            scopes: this.allEntraScopes, // Use all scopes to ensure all required scopes are granted.
        };

        if (cachedAccount) {
            console.debug('Using cached account to login user.');
            baseRequest.account = cachedAccount;
        } else if (this.identityHints?.sid) {
            console.debug('Using active session ID to login user.');
            baseRequest.sid = this.identityHints.sid;
        } else if (this.identityHints?.loginHint) {
            console.debug('Using login hint to login user.');
            baseRequest.loginHint = this.identityHints.loginHint;
        }

        // Ensure the user is logged in
        // 1. Attempt silent SSO using our base request
        try {
            console.debug('Attempting silent SSO (ssoSilent)');
            const authResult = await this.msalInstance.ssoSilent(baseRequest);

            console.info('Silent SSO successful');
            loggedInAccount = authResult.account;
        } catch (error) {
            if (error instanceof msal.InteractionRequiredAuthError) {
                console.debug('No active session detected. Interactive login required.');
            } else if (isMsalServerError(error)) {
                const errorCategory = detectAadErrorCategory(error.errorMessage);

                switch (errorCategory) {
                    case AadErrorCategory.ClientIdNotFound:
                        console.error('The configured App registration(ClientID) could not be found.', error);
                        throw new MsalAuthError(MsalAuthErrorCode.AuthenticationFailed, error);
                        break;
                    case AadErrorCategory.EntraConfiguration:
                        console.error('There is a problem with the appRegistrations redirectURI.', error);
                        throw new MsalAuthError(MsalAuthErrorCode.ConfigurationError, error);
                        break;
                    default:
                        console.error('Error during silent SSO.', error);
                        throw new MsalAuthError(MsalAuthErrorCode.AuthenticationFailed, error);
                        break;
                }
            } else {
                console.error('Error during silent SSO.', error);

                // Something went wrong logging the user in and it wasn't interaction-required,
                // so no point trying with a popup.
                throw new MsalAuthError(MsalAuthErrorCode.AuthenticationFailed, error);
            }
        }

        if (loggedInAccount) {
            console.debug(`Login successful for user ${loggedInAccount.username}`);
            this.updateLoginHint(loggedInAccount.username);
            return loggedInAccount;
        }

        // 2. Fallback: prompt the user to sign in interactively
        try {
            console.debug('Launching interactive login (loginPopup)');
            const authResult = await this.msalInstance.loginPopup(baseRequest);

            console.info('Interactive login successful');
            loggedInAccount = authResult.account;
        } catch (popupError) {
            console.error('Error during interactive login.', popupError);

            // Something went wrong logging the user in interactively
            throw new MsalAuthError(MsalAuthErrorCode.AuthenticationFailed, popupError);
        }

        if (loggedInAccount) {
            console.debug(`Login successful for user ${loggedInAccount.username}`);
            this.updateLoginHint(loggedInAccount.username);
            return loggedInAccount;
        }

        // Something unexpected went wrong logging in the user
        console.error('Unexpected error during login. No logged in account was returned.');
        throw new MsalAuthError(MsalAuthErrorCode.AuthenticationFailed);
    }

    /**
     * update the loginHint using the supplied value, if there is no loginHint currently set.
     * (if there is an existng hint, leave it as is - it's probably the same as the param anyway!)
     * @param loginName - the potential new username hint
     */
    private updateLoginHint(loginName: string): void {
        if (this.identityHints) {
            if (this.identityHints.loginHint) {
                if (this.identityHints.loginHint.length === 0) {
                    console.debug(`set login hint to ${loginName}`);
                    this.identityHints.loginHint = loginName;
                } else {
                    console.debug(`leaving login hint as ${this.identityHints.loginHint}`);
                }
            } else {
                console.debug(`set login hint to ${loginName}`);
                this.identityHints.loginHint = loginName;
            }
        }
    }

    /**
     * Get an access token for graph. Tokens are retrieved from cache if available or fetched from the server using silent or popup method.
     *
     * @throws If token acquisition fails.
     */
    public async getGraphAccessToken(): Promise<string> {
        const result = await this.acquireToken(this.entraAccessRequestScopes);
        return result.accessToken;
    }

    /**
     * Get an Entra ID token, tokens are retrieved from cache if available or fetched from the server using silent or popup method.
     *
     * @throws If token acquisition fails.
     */
    public async getUserIdToken(): Promise<string> {
        try {
            const result = await this.acquireToken(this.entraIDRequestScopes, true);
            return result.idToken;
        } catch (error) {
            if (error instanceof MsalAuthError && error.code === MsalAuthErrorCode.InvalidIdToken) {
                // We have failed to get an ID token so try to get an access token for the SSO API endpoint
                const apiScope = ServiceConstantsProvider.getSsoApiScope();
                const result = await this.acquireToken([apiScope]);

                return result.accessToken;
            }

            throw error;
        }
    }

    /**
     * Acquires a token for the given scopes.
     * Tries silent acquisition first, falls back to popup if needed.
     */
    private async acquireToken(scopes: string[], refreshExpiredIdToken = false): Promise<msal.AuthenticationResult> {
        console.info(`Requesting token for scopes: [${scopes.join(', ')}]`);

        const account = this.getCachedAccount();
        if (!account) {
            throw new MsalAuthError(MsalAuthErrorCode.NoAccount);
        }

        const request: BaseAuthRequest = {
            scopes,
            account,
        };

        try {
            console.debug('Attempting silent token acquisition');
            let authResult = await this.msalInstance.acquireTokenSilent(request);

            if (refreshExpiredIdToken && !this.hasValidIdToken(authResult)) {
                // MSAL (especially new outlook for windows) sometimes returns expired ID Tokens
                // If this happens try forcing it to refresh
                console.debug('MSAL returned an invalid or expired ID token. Retrying with forceRefresh.');
                authResult = await this.msalInstance.acquireTokenSilent({ ...request, ...{ forceRefresh: true } });

                if (!this.hasValidIdToken(authResult)) {
                    throw new MsalAuthError(MsalAuthErrorCode.InvalidIdToken, {
                        fromCache: authResult.fromCache,
                        expiresOn: authResult.expiresOn,
                        idTokenClaims: authResult.idTokenClaims,
                    });
                }
            }

            return authResult;
        } catch (error) {
            if (error instanceof msal.InteractionRequiredAuthError) {
                console.debug('Silent token acquisition failed. Falling back to popup.');
                return await this.msalInstance.acquireTokenPopup(request);
            }

            if (error instanceof MsalAuthError) {
                throw error;
            }

            console.error('Unexpected error during token acquisition', error);
            throw new MsalAuthError(MsalAuthErrorCode.TokenAcquisitionFailed, error);
        }
    }

    private hasValidIdToken(authResult: AuthenticationResult): boolean {
        const claimsWithExpiry = authResult.idTokenClaims as { exp?: number };
        if (claimsWithExpiry.exp) {
            return claimsWithExpiry.exp >= Date.now() / 1000; // Convert to seconds
        }

        return false;
    }
    private getCachedAccount(): msal.AccountInfo | null {
        console.debug('Checking for cached account');

        const hints = this.identityHints;
        if (!hints) {
            console.debug('No identity hints available');
            return null;
        }

        // 1. Try by homeAccountId (most specific)
        if (hints.homeAccountId) {
            console.debug(`try account id hint:${hints.homeAccountId}`);
            const account = this.msalInstance.getAccount({
                homeAccountId: hints.homeAccountId,
            });
            if (account) {
                console.debug('Found account by homeAccountId');
                return account;
            }
        }

        // 2. Try by loginHint (username match)
        if (hints.loginHint) {
            const loginHint = hints.loginHint.toLowerCase();
            console.debug(`try login hint:${loginHint}`);
            const account =
                this.msalInstance.getAllAccounts().find((a) => a.username.toLowerCase() === loginHint) || null;

            if (account) {
                console.debug('Found account by loginHint');
                return account;
            } else {
                console.debug('No matching account found by loginHint');
            }
        }

        console.debug('No matching cached account found');
        return null;
    }
}

// Generic “looks like an MSAL error” guard
function isMsalAuthError(e: unknown): e is AuthError {
    return !!e && typeof e === 'object' && 'errorCode' in e && 'errorMessage' in e;
}

function isMsalServerError(e: unknown): e is ServerError {
    return isMsalAuthError(e) && 'subError' in e; // ServerError extends AuthError
}

function detectAadErrorCategory(msg: string): AadErrorCategory {
    if (/AADSTS700046/i.test(msg)) return AadErrorCategory.EntraConfiguration; // Invalid Reply Address
    if (/AADSTS700016/i.test(msg)) return AadErrorCategory.ClientIdNotFound; // Invalid Reply Address
    if (/temporarily unavailable|timeout|network/i.test(msg)) return AadErrorCategory.Network;
    return AadErrorCategory.Unknown;
}

// Error types and class
enum AadErrorCategory {
    EntraConfiguration = 'EntraConfiguration',
    ClientIdNotFound = 'ClientIdNotFound',
    Network = 'Network',
    Unknown = 'Unknown',
}

export enum MsalAuthErrorCode {
    InitFailed = 'INIT_FAILED',
    ConfigurationError = 'ENTRA_MISCONFIGURATION',
    AuthenticationFailed = 'AUTHENTICATION_FAILED',
    NoAccount = 'NO_ACCOUNT',
    TokenAcquisitionFailed = 'TOKEN_ACQUISITION_FAILED',
    InvalidIdToken = 'INVALID_ID_TOKEN',
}

const errorMessages: Record<MsalAuthErrorCode, string> = {
    [MsalAuthErrorCode.InitFailed]: 'Failed to initialise MSAL instance.',
    [MsalAuthErrorCode.ConfigurationError]: 'There is a problem with the Entra Configuration',
    [MsalAuthErrorCode.AuthenticationFailed]: 'Unable to authenticate user.',
    [MsalAuthErrorCode.NoAccount]: 'No user account found. User must authenticate first.',
    [MsalAuthErrorCode.TokenAcquisitionFailed]: 'Unexpected error during token acquisition.',
    [MsalAuthErrorCode.InvalidIdToken]: 'MSAL returned an expired ID token after forced refresh.',
};

export class MsalAuthError extends Error {
    readonly code: MsalAuthErrorCode;
    readonly cause?: unknown;

    constructor(code: MsalAuthErrorCode, cause?: unknown) {
        super(errorMessages[code]);
        this.name = 'MsalAuthError';
        this.code = code;
        this.cause = cause;

        if (cause instanceof Error && cause.stack) {
            this.stack += `\nCaused by: ${cause.stack}`;
        }

        Object.setPrototypeOf(this, MsalAuthError.prototype);
    }
}
